import { CircleUserRound, LogOut, ShieldCheck } from "lucide-react";
import { useEffect, useRef } from "react";
import { AppShell } from "@/components/layout/AppShell";
import { BrandButton } from "@/components/kit/BrandButton";
import { AccessDates } from "@/components/kit/AccessDates";
import { Accent } from "@/components/kit/Accent";
import { RefreshPurchases } from "@/components/kit/RefreshPurchases";
import { SectionHeading } from "@/components/kit/SectionHeading";
import { SurfaceCard } from "@/components/kit/SurfaceCard";
import { useAuth } from "@/lib/auth";
import { ProfileSkeleton } from "@/components/kit/Skeleton";
import { track } from "@/lib/analytics";
import { PAGES } from "@/constants/testIds";

const TIER_LABEL = { basic: "Meal Plan", bundle: "Complete Meal Plan" };

/** The two Healthy styles read as their published names, never as their slugs. */
const PLAN_NAMES = {
  "healthy-flavour": "Healthy Meal Plan - Flavour Plate",
  "healthy-eating": "Healthy Meal Plan - Light Plate",
};

export default function Profile() {
  const { user, entitlements, upgrades, logout, entitlementsFromCache } = useAuth();
  const reported = useRef(false);

  useEffect(() => {
    if (reported.current) return;
    reported.current = true;
    track("profile_opened", { plans_owned: entitlements?.length ?? 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AppShell
      title="Profile"
      showBack={false}
      size="reading"
      crumbs={[{ label: "My Plans", to: "/library" }, { label: "Profile" }]}
    >
      {!user ? (
        <ProfileSkeleton />
      ) : (
      <div data-testid={PAGES.profile} className="contents">
        <SurfaceCard className="space-y-6">
          <div className="flex items-center gap-4">
            <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-secondary text-brand-tealSoft">
              <CircleUserRound className="h-6 w-6" />
            </span>
            <div className="min-w-0">
              <h1 className="font-display text-2xl font-semibold text-brand-teal">
                <Accent>{user?.name ?? "Your account"}</Accent>
              </h1>
              <p className="truncate text-sm text-muted-foreground">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-[18px] bg-secondary/60 p-4">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-orangeInk" />
            <p className="text-[13px] leading-relaxed text-brand-tealSoft">
              Your account, purchases and recipes are managed on Plattershare.com. Your meal plan app
              stores only your progress.
            </p>
          </div>
        </SurfaceCard>

        <SurfaceCard className="space-y-5">
          <SectionHeading
            eyebrow="Access"
            title="Your plans"
            note="Just bought or upgraded on Plattershare? Refresh to see it here straight away."
            action={<RefreshPurchases />}
          />
          {entitlementsFromCache && (
            <p
              data-testid="entitlements-cached-note"
              className="rounded-[18px] bg-brand-orange/[0.07] p-4 text-[13px] leading-relaxed text-brand-orangeInk"
            >
              Plattershare.com can't be reached at the moment, so your access is coming from what we
              already had. Everything you've unlocked keeps working — a brand-new purchase will appear
              as soon as the connection is back.
            </p>
          )}
          {entitlements?.length ? (
            <ul className="divide-y divide-border">
              {entitlements.map((e) => (
                <li
                  key={e.program_slug}
                  data-testid={`entitlement-${e.program_slug}`}
                  className="space-y-2 py-4"
                >
                  <div className="flex items-start justify-between gap-4">
                  <span className="min-w-0 space-y-1.5">
                    <span className="block text-[15px] font-semibold capitalize text-brand-teal">
                      {PLAN_NAMES[e.program_slug] ?? e.program_slug.replace(/-/g, " ")}
                    </span>
                    <span className="inline-block rounded-full bg-brand-tealDeep px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-white">>
                      {TIER_LABEL[e.tier] ?? e.tier}
                    </span>
                  </span>
                  <span className="flex items-center gap-2">
                    {!e.expires_at && !e.web_access_until && (
                      <span className="rounded-full bg-secondary px-3 py-1 text-[11px] font-semibold text-brand-tealSoft">
                        Lifetime
                      </span>
                    )}
                  </span>
                  </div>
                  <AccessDates access={e} slug={e.program_slug} />
                  {/* Server-decided: present only when this account holds an upgradeable purchase. */}
                  {upgrades?.[e.program_slug] && (
                    <a
                      href={upgrades[e.program_slug]}
                      target="_blank"
                      rel="noopener"
                      data-testid={`profile-upgrade-${e.program_slug}`}
                      className="ps-tap inline-flex min-h-[40px] items-center gap-1 rounded-full bg-brand-orange/10 px-4 text-[11.5px] font-bold text-brand-orangeInk transition-colors hover:bg-brand-orange/15 sm:text-[12.5px]"
                    >
                      Upgrade to Complete Plan →
                    </a>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted-foreground">No active plans on this account.</p>
          )}
          <p
            data-testid="access-delay-note"
            className="text-[12.5px] leading-relaxed text-muted-foreground"
          >
            Recently purchased or upgraded? Access may take a few minutes to update.
          </p>
        </SurfaceCard>

        <BrandButton name="logout" variant="outline" size="lg" onClick={logout}>
          <LogOut className="h-4 w-4" /> Sign out of this device
        </BrandButton>
      </div>
      )}
    </AppShell>
  );
}
